import React, { ReactNode, useCallback, useEffect, useState } from "react";
import "../ConfirmModal/ConfirmModal.less";
import "./createGroupModal.less";
import debounce from "lodash.debounce";
import { Button, Input, Modal, TextArea } from "../../../../../BP2Components";
import { useTableListContext } from "../../../../../../context/TableListContext/TableListContext";
import { FilterItem } from "../TableFIlterScripts";
import IntlMessages from "../../../../../../util/IntlMessages";
import projectConfig from "@projectConfig";

interface ICreateGroupModalProps {
   onConfirm: (filter) => void;
   onCancel: (value: boolean) => void;
   isOpen: boolean;
   currentFilter?: FilterItem | undefined;
   totalElements?: number;
   getFilterObjectCount?: () => void;
   topBarTitle: ReactNode;
   listId?: string;
}

const CreateGroupModal = ({
   onCancel,
   onConfirm,
   isOpen,
   currentFilter,
   totalElements,
   getFilterObjectCount,
   topBarTitle,
}: ICreateGroupModalProps) => {
   const { setEditedListFilters } = useTableListContext();
   const MAX_DESCRIPTION = 500;

   const [form, setForm] = useState({
      name: "",
      description: "",
   });

   useEffect(() => {
      if (currentFilter) {
         setForm({
            name: currentFilter.name || "",
            description: currentFilter.description || "",
         });
      } else {
         setForm({ name: "", description: "" });
      }
   }, [currentFilter, isOpen]);

   useEffect(() => {
      if (isOpen && getFilterObjectCount) getFilterObjectCount();
   }, [isOpen, getFilterObjectCount]);

   const handleInputChange = (field, value) => {
      if (field === "description" && value.length > MAX_DESCRIPTION) return;
      setForm((prev) => ({ ...prev, [field]: value }));
      if (currentFilter?.id) {
         debouncedChange(field, currentFilter.id, value);
      }
   };

   const changeGroupInfo = (field, filterId, value) => {
      setEditedListFilters((prev) =>
         prev.map((filter) =>
            filter.id === filterId
               ? {
                    ...filter,
                    [field]: value,
                 }
               : filter,
         ),
      );
   };

   const debouncedChange = useCallback(
      debounce((field, id, value) => {
         changeGroupInfo(field, id, value);
      }, 500),
      [],
   );

   const handleSave = () => {
      onConfirm(form);
   };

   return (
      <Modal
         isOpen={isOpen}
         width={1080}
         onCancel={onCancel}
         height={window.innerHeight / 1.05}
         bodyStyles={{ overflowX: "auto" }}
         topBarStyles={{
            justifyContent: "space-between",
            color: projectConfig.cssVariables["--topbar-text"],
            fontSize: "24px",
            fontWeight: "900",
            fontFamily: projectConfig.cssVariables["--font-primary"],
            padding: "20px",
         }}
         wrapClassName="no-resize"
         topBarContent={topBarTitle}
         onCancelButtonStyle={{ position: "relative", padding: "5px 0" }}
      >
         <div className="create-group-modal">
            <div className="choose-object-field">
               <IntlMessages id="filter.selectedObjects" />
               <strong>{totalElements ?? 0}</strong>
            </div>
            <div className="field-wrapper">
               <span>
                  <IntlMessages id="boardManager.name" />:
               </span>
               <Input onChange={(e) => handleInputChange("name", e.target.value)} />
            </div>
            <div className="field-wrapper">
               <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                  <span>
                     <IntlMessages id="settings.description" />:
                  </span>
                  <span>
                     {form.description.length}/{MAX_DESCRIPTION}
                  </span>
               </div>
               <TextArea
                  maxLength={MAX_DESCRIPTION}
                  onChange={(e) => handleInputChange("description", e.target.value)}
               />
            </div>
            <div className="create-group-modal__actions">
               <Button
                  className="confirm-modal__button confirm-modal__button--cancel"
                  onClick={() => onCancel(false)}
               >
                  <IntlMessages id="global.cancel" />
               </Button>
               <Button
                  className="confirm-modal__button confirm-modal__button--confirm"
                  onClick={handleSave}
                  disabled={!form.name}
               >
                  <IntlMessages id="global.save" />
               </Button>
            </div>
         </div>
      </Modal>
   );
};

export default CreateGroupModal;
